import { useCallback, useEffect, useRef, useState } from 'react'
import { CALL_PAGE_SIZE, callsService } from '../../../services/callsService'
import type { LiveStatus } from '../../../components/LiveIndicator'
import type { Call, Order } from '../../../types/api.types'

// Only runs while the stream is down. A healthy page never polls.
const POLL_MS = 10_000

function errorMessage(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback
}

// `page` is the newest rows, newest-first; anything kept is older than all of
// them, so concatenating preserves the order. Matching ids take the fresh copy.
function mergeCalls(prev: Call[], page: Call[]): Call[] {
  const fresh = new Map(page.map((call) => [call.id, call]))
  return [...page, ...prev.filter((call) => !fresh.has(call.id))]
}

export interface UseCallsResult {
  calls: Call[]
  // Orders as pushed by the stream, keyed by order id. Empty until an order
  // event arrives — the drawer fetches its own first copy.
  orders: Map<number, Order>
  loading: boolean
  error: string | null
  streamStatus: LiveStatus
  hasMore: boolean
  loadingMore: boolean
  loadMore: () => void
}

export function useCalls(propertyId: number | null, day?: string): UseCallsResult {
  const [calls, setCalls] = useState<Call[]>([])
  const [orders, setOrders] = useState<Map<number, Order>>(() => new Map())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [streamStatus, setStreamStatus] = useState<LiveStatus>('idle')
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)

  // Namespaced because call ids and order ids collide freely — a bare numeric
  // set would drop real events.
  const seenRef = useRef<Set<string>>(new Set())
  const offsetRef = useRef(0)

  useEffect(() => {
    if (propertyId == null) {
      setCalls([])
      setOrders(new Map())
      setLoading(false)
      setError(null)
      setStreamStatus('idle')
      setHasMore(false)
      return
    }

    let cancelled = false
    let closeStream: (() => void) | null = null
    let syncing = false

    function teardown(): void {
      closeStream?.()
      closeStream = null
    }

    function connect(): void {
      if (cancelled) return
      setStreamStatus('connecting')
      closeStream = callsService.streamLive(propertyId as number, {
        onCall: (call) => {
          if (cancelled) return
          const key = `call:${call.id}`
          if (seenRef.current.has(key)) return
          seenRef.current.add(key)
          setCalls((prev) => [call, ...prev])
        },
        // An order event is an update — a status move or a print ack — so it
        // replaces by id. Repeats are expected and wanted; deduping them would
        // freeze the card on whichever status arrived first.
        onOrder: (order) => {
          if (cancelled) return
          setOrders((prev) => new Map(prev).set(order.id, order))
        },
        onOpen: () => {
          if (cancelled) return
          setStreamStatus('live')
          setError(null)
        },
        onError: ({ status, fatal }) => {
          if (cancelled) return
          if (!fatal) {
            setStreamStatus('reconnecting')
            return
          }
          setStreamStatus('error')
          if (status === 401) setError('Your session expired — please sign in again.')
        },
        onAuthRevoked: () => {
          if (cancelled) return
          setStreamStatus('error')
          setError('Your access to this property was removed.')
        },
      })
    }

    // The REST read is awaited before the stream opens, every time. A fetch
    // resolving after live events would overwrite them with an older snapshot,
    // so the stream is torn down first rather than raced.
    async function resync(initial: boolean): Promise<void> {
      if (syncing) return
      syncing = true
      teardown()
      if (initial) setLoading(true)
      try {
        const page = await callsService.list(propertyId as number, { day })
        if (cancelled) return
        for (const call of page) seenRef.current.add(`call:${call.id}`)
        setCalls((prev) => (initial ? page : mergeCalls(prev, page)))
        setHasMore(page.length === CALL_PAGE_SIZE)
        if (initial) offsetRef.current = page.length
        setError(null)
      } catch (e) {
        if (cancelled) return
        if (initial) {
          setCalls([])
          setError(errorMessage(e, 'Failed to load conversations'))
        }
      } finally {
        syncing = false
        if (!cancelled && initial) setLoading(false)
      }
      if (!cancelled) connect()
    }

    seenRef.current = new Set()
    offsetRef.current = 0
    resync(true)

    // A sleeping desktop loses everything past the server's 60 second replay,
    // and a publish that failed on their side is gone permanently — this refetch
    // is the only thing that ever corrects either.
    function onWake(): void {
      if (document.visibilityState === 'visible') resync(false)
    }
    document.addEventListener('visibilitychange', onWake)
    window.addEventListener('online', onWake)

    return () => {
      cancelled = true
      teardown()
      document.removeEventListener('visibilitychange', onWake)
      window.removeEventListener('online', onWake)
    }
  }, [propertyId, day])

  // Fallback for a stream that will not stay up — the server answers 503 when
  // Redis is down, and EventSource reports that as an untyped failure like any
  // other. Either way the answer is the same: stop claiming Live, and poll.
  useEffect(() => {
    if (propertyId == null || streamStatus !== 'reconnecting') return
    const timer = setInterval(() => {
      callsService
        .list(propertyId, { day })
        .then((page) => {
          for (const call of page) seenRef.current.add(`call:${call.id}`)
          setCalls((prev) => mergeCalls(prev, page))
        })
        .catch(() => {})
    }, POLL_MS)
    return () => clearInterval(timer)
  }, [propertyId, day, streamStatus])

  // ponytail: offset paging against a head that grows can re-serve rows we
  // already hold. Deduping on the way in is cheaper than tracking a cursor the
  // API does not offer.
  const loadMore = useCallback(() => {
    if (propertyId == null || loadingMore) return
    setLoadingMore(true)
    callsService
      .list(propertyId, { day, offset: offsetRef.current })
      .then((page) => {
        for (const call of page) seenRef.current.add(`call:${call.id}`)
        setCalls((prev) => {
          const known = new Set(prev.map((call) => call.id))
          return [...prev, ...page.filter((call) => !known.has(call.id))]
        })
        offsetRef.current += page.length
        setHasMore(page.length === CALL_PAGE_SIZE)
      })
      .catch((e: unknown) => setError(errorMessage(e, 'Could not load more conversations')))
      .finally(() => setLoadingMore(false))
  }, [propertyId, day, loadingMore])

  return { calls, orders, loading, error, streamStatus, hasMore, loadingMore, loadMore }
}
